import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { PermissionsEnum } from "@/auth/interfaces";
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTimeCell,
  hasAllPermissions,
  hasPermission,
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from "@/shared";
import { useUpdateEmpresa, usefetchEmpresas } from "@/shared/actions/erp";
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
  SwitchComponente,
} from "@/shared/components/ui";
import { Empresa } from "@/shared/interfaces";
import { useUiConfirmModalStore } from "@/store/ui";
import { Avatar } from "@mui/material";
import { SAVE_EMPRESA_PEMISSIONS } from "../../shared/constants";

export interface EmpresasPageProps {}

const EmpresasPage: React.FC<EmpresasPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_empresa);
  const returnUrlEmpresasPage = ROUTER_PATHS.administration.empresaNav;

  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen
  );

  ///* table -----------------------
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data -----------------------
  const {
    data: coberturasPagingRes,
    isLoading,
    isRefetching,
  } = usefetchEmpresas({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations -----------------------
  const updSate = useUpdateEmpresa();

  ///* handlers -----------------------
  const onEdit = (original: Empresa) => {
    navigate(`${returnUrlEmpresasPage}/editar/${original.id_empresa}`);
  };

  ///* columns
  const columns = useMemo<MRT_ColumnDef<Empresa>[]>(
    () => [
      {
        accessorKey: "url_logo",
        header: "LOGO",
        size: 60,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return row.original?.url_logo ? (
            <Avatar
              sx={{
                height: 40,
                width: 40,
              }}
              src={row.original.url_logo}
              alt="logo"
            />
          ) : (
            "N/A"
          );
        },
      },

      {
        accessorKey: "identificacion",
        header: "RUC",
        size: 150,
        Cell: ({ row }) => emptyCellOneLevel(row, "identificacion"),
      },
      {
        accessorKey: "razon_social",
        header: "Razón Social",
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, "razon_social"),
      },
      {
        accessorKey: "nombre_comercial",
        header: "Nombre Comercial",
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, "nombre_comercial"),
      },

      // {
      //   accessorKey: 'url_logo',
      //   header: 'LOGO',
      //   size: 180,
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   Cell: ({ row }) => {
      //     return (
      //       <SingleImageAndBtnCellModal
      //         url={row.original.url_logo}
      //         title="Logo"
      //       />
      //     );
      //   },
      // },

      {
        accessorKey: "email",
        header: "Email",
        size: 180,
        Cell: ({ row }) => emptyCellOneLevel(row, "email"),
      },
      {
        accessorKey: "direccion",
        header: "Dirección",
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, "direccion"),
      },
      {
        accessorKey: "contador",
        header: "Contador",
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, "contador"),
      },
      {
        accessorKey: "telefono_1",
        header: "Teléfono 1",
        size: 120,
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_1"),
      },
      {
        accessorKey: "telefono_2",
        header: "Teléfono 1",
        size: 120,
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_2"),
      },
      {
        accessorKey: "telefono_3",
        header: "Teléfono 1",
        size: 120,
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_3"),
      },

      {
        accessorKey: "razon_social_representante",
        header: "Razón Social Representante Legal",
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, "razon_social_representante"),
      },
      {
        accessorKey: "identificacion_representante",
        header: "Identificación Representante Legal",
        size: 150,
        Cell: ({ row }) =>
          emptyCellOneLevel(row, "identificacion_representante"),
      },
      {
        accessorKey: "email_representante",
        header: "Email Representante Legal",
        size: 150,
        Cell: ({ row }) => emptyCellOneLevel(row, "email_representante"),
      },
      {
        accessorKey: "telefono_representante",
        header: "Teléfono Representante Legal",
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, "telefono_representante"),
      },

      {
        accessorKey: "es_agente_retencion",
        header: "Es Agente de Retención",
        size: 90,
        Cell: ({ row }) => {
          return row.original?.es_agente_retencion ? "SI" : "NO";
        },
      },
      {
        accessorKey: "numero_agente_retencion",
        header: "Número Agente de Retención",
        size: 90,
        Cell: ({ row }) => emptyCellOneLevel(row, "numero_agente_retencion"),
      },
      {
        accessorKey: "tipo_regimen",
        header: "Tipo Régimen",
        size: 120,
        Cell: ({ row }) =>
          row.original?.tipo_regimen_data?.nombre ||
          row.original?.tipo_regimen ||
          "N/A",
      },
      {
        accessorKey: "crea_ats",
        header: "Crea ATS",
        size: 60,
        Cell: ({ row }) => {
          return row.original?.crea_ats ? "SI" : "NO";
        },
      },

      ///* fk
      {
        accessorKey: "pais",
        header: "País",
        size: 180,
        Cell: ({ row }) => row.original?.pais_data?.nombre || "N/A",
      },
      {
        accessorKey: "provincia",
        header: "Provincia",
        size: 180,
        Cell: ({ row }) => row.original?.provincia_data?.nombre || "N/A",
      },
      {
        accessorKey: "ciudad",
        header: "Ciudad",
        size: 180,
        Cell: ({ row }) => row.original?.ciudad_data?.nombre || "N/A",
      },
      {
        accessorKey: "zona",
        header: "Zona",
        size: 180,
        Cell: ({ row }) => row.original?.zona_data?.nombre || "N/A",
      },
      {
        accessorKey: "sector",
        header: "Sector",
        size: 180,
        Cell: ({ row }) => row.original?.sector_data?.nombre || "N/A",
      },

      {
        accessorKey: "codigo",
        header: "Código",
        size: 210,
        Cell: ({ row }) => emptyCellOneLevel(row, "codigo"),
      },

      //
      {
        accessorKey: "estado",
        header: "Estado",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
          return (
            <SwitchComponente
              estadoSwitch={row.original?.estado}
              titulo={row.original?.estado ? "Activo" : "Inactivo"}
              setEstadoSwitch={(state) => {
                if (!hasPermission(PermissionsEnum.api_change_empresa)) return;

                setConfirmDialog({
                  isOpen: true,
                  title: "Cambiar Estado",
                  subtitle:
                    "¿Está seguro que desea cambiar el estado de este registro ?",
                  onConfirm: () => {
                    setConfirmDialogIsOpen(false);
                    updSate.mutate({
                      id: row.original?.id_empresa!,
                      data: { estado: !!state } as any,
                    });
                  },
                });
              }}
            />
          );
        },
      },
      {
        accessorKey: "fecha_creacion",
        header: "Fecha de Creación",
        size: 200,
        enableColumnFilter: true,
        filterVariant: "date",
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, "fecha_creacion"),
      },
      {
        accessorKey: "fecha_modificacion",
        header: "Fecha de Modificación",
        size: 200,
        enableColumnFilter: true,
        filterVariant: "date",
        enableSorting: false,
        Cell: ({ row }) => formatDateWithTimeCell(row, "fecha_modificacion"),
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene
      title="Empresas"
      urlRedirect={`${returnUrlEmpresasPage}/crear`}
      enableRedirect={hasAllPermissions([
        ...SAVE_EMPRESA_PEMISSIONS,
        PermissionsEnum.api_add_empresa,
      ])}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        cleanFilter={cleanFilter}
        text="empresa por nombre comercial"
      />

      <GeneralTable<Empresa>
        columns={columns}
        data={coberturasPagingRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={coberturasPagingRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          ...SAVE_EMPRESA_PEMISSIONS,
          PermissionsEnum.api_change_flota,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([
          ...SAVE_EMPRESA_PEMISSIONS,
          PermissionsEnum.api_change_plan,
        ])}
        onEdit={onEdit}
        canDelete={false}
      />
    </SingleTableBoxScene>
  );
};

export default EmpresasPage;
